import flatten from 'lodash/flatten';
import React from 'react';
import useUpdateEffect from 'react-use/lib/useUpdateEffect';
import { WindowScroller } from 'react-virtualized';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import List, { ListRowRenderer } from 'react-virtualized/dist/commonjs/List';

import { Image, ShowroomSectionInfoFragment, MediaInfoFragment, Video } from 'generated/graphql';

import CustomResizeObserver from 'lib/ResizeObserver';
import { SizeCalculator } from 'lib/flexSizeCalculator';
import { ImageSizeValues } from 'lib/mediaThresholds';
import { useGetShowroomScrollElement } from 'lib/showroom';

import useTheme from '@material-ui/core/styles/useTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { showroomImageLabelHeight } from 'components/Showroom/Content/MediaItem/styles';

import { useStyles } from './styles';

type ImageProps = {
	sizes: ImageSizeValues;
};

export type RenderItemFn = (item: MediaInfoFragment, imageProps: ImageProps) => React.ReactElement;

export type ComponentProps = {
	currentSection: ShowroomSectionInfoFragment;
	renderItem: RenderItemFn;
};

const getElementRatio = element => (1.0 * element.width) / element.height;

type RowMedia = ShowroomSectionInfoFragment['rows'][0]['medias'][0];

const ShowroomContentSectionRowLayout = (props: ComponentProps) => {
	const { currentSection } = props;
	const { renderItem } = props;

	const spacing = currentSection?.options.spacing;

	const theme = useTheme();
	const mobileVersion = useMediaQuery(theme.breakpoints.down('sm'));

	const classes = useStyles({
		spacing
	});

	const containerRef = React.useRef<HTMLDivElement>();
	const listRef = React.useRef<List>();

	const scrollElement = useGetShowroomScrollElement();

	const [containerWidth, setContainerWidth] = React.useState(1500);

	const calculateElementWidth = React.useCallback(
		// We set the minWidth in mobile version, otherwise this will be 0 (all images will placed in the same row)
		(element: RowMedia) => (mobileVersion ? Math.min(Math.ceil(containerWidth * getElementRatio(element) * 0.5), containerWidth) : 0),
		[containerWidth, mobileVersion]
	);

	const calculateFlexGrow = React.useCallback((element: RowMedia) => getElementRatio(element) * 2, []);

	const sizeCalculator = React.useMemo(
		() => new SizeCalculator<any>({ getWidth: calculateElementWidth, getFlexGrow: calculateFlexGrow }),
		[calculateElementWidth, calculateFlexGrow]
	);

	const rows = React.useMemo(() => {
		if (mobileVersion) {
			const allMedias = flatten(currentSection?.rows.map(row => row.medias));
			return sizeCalculator.buildAllRows(allMedias, containerWidth, (containerWidth * spacing) / 100);
		} else return currentSection?.rows.map(row => row.medias) || [];
	}, [containerWidth, currentSection?.rows, mobileVersion, sizeCalculator, spacing]);

	const recompute = React.useCallback(() => {
		setContainerWidth(containerRef.current.clientWidth);
		listRef.current.recomputeRowHeights();
	}, []);

	React.useEffect(() => {
		setContainerWidth(containerRef.current.clientWidth);
	}, []);

	useUpdateEffect(() => {
		recompute();
	}, [currentSection?.rows, recompute]);

	React.useEffect(() => {
		const { current: containerElement } = containerRef;
		const observer = new CustomResizeObserver(() => {
			recompute();
		});
		observer.observe(containerElement);
		return () => {
			observer.unobserve(containerElement);
		};
	}, [recompute]);

	const buildRow: ListRowRenderer = React.useCallback(
		({ index, style }) => {
			const row = rows[index];
			const ratios = row.map((media: Image | Video) => media.width / media.height);
			const totalRatioSum = ratios.reduce((total, currentRatio) => total + currentRatio, 0);
			return (
				<div key={`mediaGridRow_${index}`} style={style} className={classes.showroomGridRow}>
					{row.map((media, mediaIndex) => {
						const sizes: ImageSizeValues = [100, (ratios[mediaIndex] / totalRatioSum) * 100];
						const minWidth = Math.min(ratios[mediaIndex] * 50, 100);
						return (
							<div
								className={classes.showroomGridItem}
								key={`${media.mediaType}_${media.cloudinaryId}_${mediaIndex}`}
								style={{
									flex: `${ratios[mediaIndex] * 2} 1`,
									minWidth: `${minWidth}%`
								}}
							>
								{renderItem(media, { sizes })}
							</div>
						);
					})}
				</div>
			);
		},
		[rows, classes, renderItem]
	);

	const computeRowHeight = React.useCallback(
		({ index }: { index: number }): number => {
			const row = rows[index];
			return sizeCalculator.computeRowHeight(row, containerWidth, currentSection?.options.displayLabel ? showroomImageLabelHeight : 0);
		},
		[containerWidth, rows, sizeCalculator, currentSection]
	);

	return (
		<div className={classes.showroomGridRowDisplayContainer} ref={containerRef}>
			<WindowScroller scrollElement={scrollElement}>
				{({ height, isScrolling, onChildScroll, scrollTop, registerChild }) => (
					<AutoSizer disableHeight style={{ height: 'auto' }}>
						{({ width }) => (
							<div ref={registerChild}>
								<List
									ref={listRef}
									autoHeight
									width={width}
									height={height}
									rowCount={rows.length}
									rowHeight={computeRowHeight}
									rowRenderer={buildRow}
									isScrolling={isScrolling}
									onScroll={onChildScroll}
									scrollTop={scrollTop}
								></List>
							</div>
						)}
					</AutoSizer>
				)}
			</WindowScroller>
		</div>
	);
};

export default React.memo(ShowroomContentSectionRowLayout);
